// Registers the service worker (web only) and applies new versions by itself.
//
// - The app checks for a new version at startup, when it becomes visible again
//   (tab focus, installed PWA resumed from the background, back/forward cache),
//   when the device comes back online, and every 30 minutes while visible.
//   Client-side route changes are not navigations, so without these checks the
//   browser would only look for an update on a full page load.
// - A downloaded version waits (see service-worker.ts). The page switches to it
//   at a safe moment and then reloads exactly once.
// - Native Capacitor builds ship their web files inside the app binary and never
//   use a service worker.

import { Capacitor } from '@capacitor/core';

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

// When the current screen can be reloaded to apply a new version:
// 'now' - nothing on screen would be lost;
// 'when-idle' - only once the user has stopped touching the page for a while;
// 'never' - not until they leave the screen.
export type ReloadPolicy = 'now' | 'when-idle' | 'never';

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  reloadPolicy?: () => ReloadPolicy;
};

const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MIN_UPDATE_CHECK_GAP_MS = 60 * 1000;
const PENDING_UPDATE_RETRY_MS = 5 * 1000;
const RELOAD_GUARD_KEY = 'resonance-sw-update-reload-at';
const RELOAD_GUARD_MS = 30 * 1000;
const PERMISSION_PROMPT_HOLD_MS = 60 * 1000;
// How long the user has to leave the page alone before a 'when-idle' screen reloads.
const IDLE_SAFE_MS = 60 * 1000;

// Ionic overlays are only in the DOM while they are presented.
const OPEN_OVERLAY_SELECTOR = [
  'ion-modal',
  'ion-alert',
  'ion-action-sheet',
  'ion-popover',
  'ion-picker',
  'ion-loading',
]
  .map((tag) => `${tag}:not(.overlay-hidden)`)
  .join(', ');

let swRegistration: ServiceWorkerRegistration | null = null;
// A new worker already controls this page, which still runs the old code.
let reloadPending = false;
let reloading = false;
// 0 until the user first touches the page, so a freshly loaded page counts as idle.
let lastInteractionAt = 0;
let retryTimer: number | undefined;
let lastUpdateCheck = 0;
let reloadHolds = 0;

// Screens call this while they show something an automatic update reload would
// wipe (for example the reward sequence after a questionnaire), and call the
// returned function once it is gone. With `maxMs`, the hold also ends by itself.
export function holdUpdateReload(maxMs?: number) {
  reloadHolds += 1;
  let released = false;
  let timer: number | undefined;
  const release = () => {
    window.clearTimeout(timer);
    if (!released) {
      released = true;
      reloadHolds -= 1;
    }
  };
  if (maxMs !== undefined) {
    timer = window.setTimeout(release, maxMs);
  }
  return release;
}

// Call before asking for notification permission (Notification.requestPermission
// or pushManager.subscribe). Chrome counts a prompt closed by a reload as ignored
// and blocks the site after a few, so an open prompt delays the reload a while.
export function holdUpdateReloadForPermissionPrompt() {
  return holdUpdateReload(PERMISSION_PROMPT_HOLD_MS);
}

export function register(config: Config = {}) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    if (Capacitor.isNativePlatform()) {
      // A store update replaces the bundled files; a service worker cache could
      // outlive it and serve old files, so remove any registration left behind.
      unregisterAll();
      return;
    }

    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    // Attach before any worker can take control, so no controllerchange is missed.
    listenForControllerChange(config);
    listenForUserInteraction();

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://cra.link/PWA'
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

function showNotification() {
  // iOS Safari tabs and Android WebView have no Notification API at all, and
  // asking for permission must never break service worker registration.
  if (!('Notification' in window) || Notification.permission !== "default") {
    return;
  }
  const answered = holdUpdateReloadForPermissionPrompt();
  try {
    const request = Notification.requestPermission((result) => {
      answered();
      if (result === "granted") {
        navigator.serviceWorker.ready.then((registration) => {
          registration.showNotification("Notification is active", {
            body: "Now you will receive notifications",
            // vibrate is not part of NotificationOptions, so it is removed
            tag: "vibration-sample",
          });
        });
      }
    });
    // Older Safari returns undefined instead of a promise.
    if (request) {
      request.then(answered, answered);
    }
  } catch (error) {
    answered();
    console.warn('Cannot request notification permission', error);
  }
}

function registerValidSW(swUrl: string, config: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      swRegistration = registration;
      watchForNewVersion(registration, config);
      scheduleUpdateChecks(registration, config);
      // While a new version replaces the current one, a prompt opened now would
      // only hold up the update reload; the reloaded page asks instead.
      if (!(registration.active && (registration.waiting || registration.installing))) {
        showNotification();
      }
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function watchForNewVersion(registration: ServiceWorkerRegistration, config: Config) {
  // The worker this origin already had when the page registered; null on a first visit.
  const previousWorker = registration.active;

  const onInstalled = (worker: ServiceWorker) => {
    const controlled = Boolean(navigator.serviceWorker.controller);
    if (!controlled && (previousWorker == null || previousWorker === worker)) {
      console.log('Content is cached for offline use.');
      if (config.onSuccess) {
        config.onSuccess(registration);
      }
      return;
    }

    console.log('New content is available and will be used at the next safe moment.');
    if (config.onUpdate) {
      config.onUpdate(registration);
    }
    if (!controlled) {
      // Hard-reloaded page (not controlled by the worker): no controllerchange
      // tells it about the new version, so watch the worker's activation instead.
      worker.addEventListener('statechange', () => {
        if (worker.state === 'activated') {
          reloadPending = true;
          applyPendingUpdate(config);
        }
      });
    }
    applyPendingUpdate(config);
  };

  // Downloaded during an earlier visit and still waiting.
  if (registration.waiting) {
    onInstalled(registration.waiting);
  }

  registration.addEventListener('updatefound', () => {
    const installingWorker = registration.installing;
    if (installingWorker == null) return;

    installingWorker.addEventListener('statechange', () => {
      if (installingWorker.state === 'installed') {
        onInstalled(installingWorker);
      }
    });
  });
}

function listenForControllerChange(config: Config) {
  let hadController = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) {
      // First install: clientsClaim() took control of a page that already runs
      // the version this worker serves. Nothing to reload.
      hadController = true;
      return;
    }
    reloadPending = true;
    applyPendingUpdate(config);
  });
}

function listenForUserInteraction() {
  const markInteracted = () => {
    lastInteractionAt = Date.now();
  };
  ['pointerdown', 'touchstart', 'keydown'].forEach((type) => {
    window.addEventListener(type, markInteracted, { capture: true, passive: true });
  });
}

function isEditingText(): boolean {
  const element = document.activeElement as HTMLElement | null;
  if (!element) {
    return false;
  }
  const tag = element.tagName;
  return (
    element.isContentEditable ||
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    tag === 'ION-INPUT' ||
    tag === 'ION-TEXTAREA' ||
    tag === 'ION-SEARCHBAR'
  );
}

function isSafeToReloadNow(config: Config): boolean {
  if (reloadHolds > 0 || isEditingText() || document.querySelector(OPEN_OVERLAY_SELECTOR)) {
    return false;
  }
  const policy = config.reloadPolicy ? config.reloadPolicy() : 'when-idle';
  if (policy === 'never') {
    return false;
  }
  if (policy === 'now') {
    return true;
  }
  // A page nobody has touched for a while, including one just loaded, can be
  // reloaded without interrupting anything.
  return Date.now() - lastInteractionAt >= IDLE_SAFE_MS;
}

function hasPendingUpdate(): boolean {
  return reloadPending || Boolean(swRegistration && swRegistration.waiting);
}

function msSinceUpdateReload(): number {
  try {
    return Date.now() - (Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0);
  } catch (error) {
    // Storage blocked: the in-memory `reloading` flag still prevents a double reload.
    return Infinity;
  }
}

function applyPendingUpdate(config: Config) {
  if (reloading || !hasPendingUpdate()) {
    return;
  }
  // At most one update reload per tab every 30 s, for example while two builds
  // are served alternately during a deploy. The update stays pending meanwhile.
  if (!isSafeToReloadNow(config) || msSinceUpdateReload() < RELOAD_GUARD_MS) {
    retryLater(config);
    return;
  }
  if (reloadPending) {
    reloadOnce();
    return;
  }
  const waitingWorker = swRegistration && swRegistration.waiting;
  if (waitingWorker) {
    // Activating it fires controllerchange, which reloads the page.
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  }
}

function retryLater(config: Config) {
  if (retryTimer !== undefined) {
    return;
  }
  retryTimer = window.setInterval(() => {
    if (reloading || !hasPendingUpdate()) {
      window.clearInterval(retryTimer);
      retryTimer = undefined;
      return;
    }
    applyPendingUpdate(config);
  }, PENDING_UPDATE_RETRY_MS);
}

function reloadOnce() {
  if (reloading) {
    return;
  }
  reloading = true;
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch (error) {
    // Ignore.
  }
  window.location.reload();
}

function checkForUpdate(registration: ServiceWorkerRegistration, force = false) {
  const now = Date.now();
  if (!force && now - lastUpdateCheck < MIN_UPDATE_CHECK_GAP_MS) {
    return;
  }
  lastUpdateCheck = now;
  registration.update().catch(() => {
    // Offline or server unavailable: the next trigger retries.
  });
}

function scheduleUpdateChecks(registration: ServiceWorkerRegistration, config: Config) {
  checkForUpdate(registration, true);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkForUpdate(registration);
    }
    // Leaving or coming back are both good moments to apply a deferred update.
    applyPendingUpdate(config);
  });

  // Restored from the back/forward cache: no navigation, so no browser check.
  window.addEventListener('pageshow', (event: PageTransitionEvent) => {
    if (event.persisted) {
      checkForUpdate(registration, true);
    }
  });

  // A desktop tab that is never hidden (switching to another app usually leaves it
  // "visible") gets no visibilitychange, so also check when the window regains focus.
  window.addEventListener('focus', () => checkForUpdate(registration));

  window.addEventListener('online', () => checkForUpdate(registration));

  window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      checkForUpdate(registration);
    }
  }, UPDATE_CHECK_INTERVAL_MS);
}

function checkValidServiceWorker(swUrl: string, config: Config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

function unregisterAll() {
  navigator.serviceWorker
    .getRegistrations()
    .then((registrations) =>
      Promise.all(registrations.map((registration) => registration.unregister()))
    )
    .catch(() => undefined);
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
